import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, X } from "lucide-react";
import { tx } from "@/i18n";

function Thumbs({ shots, onRemove }) {
  if (!shots.length) return null;
  return (
    <div className="flex gap-2 overflow-x-auto py-1" data-testid="camera-shots">
      {shots.map((s, i) => (
        <div key={s.url} className="relative shrink-0">
          <img src={s.url} alt="" className="h-14 w-14 rounded-md object-cover border border-white/30" />
          <button type="button" onClick={() => onRemove(i)} className="absolute -top-1.5 -right-1.5 bg-destructive text-white rounded-full p-0.5" data-testid={`camera-shot-remove-${i}`}><X className="h-3 w-3" /></button>
        </div>
      ))}
    </div>
  );
}

// Live camera die openblijft: meerdere foto's achter elkaar, daarna in één keer uploaden.
export function CameraDialog({ open, onClose, onDone, onFallback }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [shots, setShots] = useState([]);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1440 } }, audio: false })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => { onClose(); onFallback(); });
    if (!navigator.mediaDevices?.getUserMedia) { onClose(); onFallback(); }
    return () => { cancelled = true; streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => { shots.forEach((s) => URL.revokeObjectURL(s.url)); setShots([]); onClose(); };
  const shoot = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `foto-${Date.now()}.jpg`, { type: "image/jpeg" });
      setShots((p) => [...p, { file, url: URL.createObjectURL(blob) }]);
    }, "image/jpeg", 0.85);
    setFlash(true); setTimeout(() => setFlash(false), 120);
  };
  const done = () => { const files = shots.map((s) => s.file); close(); if (files.length) onDone(files); };
  const remove = (i) => setShots((p) => { URL.revokeObjectURL(p[i].url); return p.filter((_, j) => j !== i); });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="p-0 bg-black border-0 max-w-lg w-full sm:rounded-xl overflow-hidden gap-0 [&>button]:text-white" data-testid="camera-dialog">
        <DialogTitle className="sr-only">{tx("Foto maken")}</DialogTitle>
        <DialogDescription className="sr-only">{tx("Maak meerdere foto's achter elkaar")}</DialogDescription>
        <div className="relative bg-black">
          <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-[65vh] object-contain" data-testid="camera-video" />
          {flash && <div className="absolute inset-0 bg-white/70 pointer-events-none" />}
        </div>
        <div className="p-3 space-y-3 bg-black">
          <Thumbs shots={shots} onRemove={remove} />
          <div className="flex items-center justify-between gap-3">
            <Button variant="ghost" onClick={close} className="text-white hover:bg-white/10" data-testid="camera-cancel">{tx("Annuleren")}</Button>
            <button type="button" onClick={shoot} aria-label={tx("Foto maken")} className="h-16 w-16 rounded-full border-4 border-white bg-white/20 active:scale-90 transition-transform" data-testid="camera-shutter" />
            <Button onClick={done} disabled={!shots.length} className="gap-1.5" data-testid="camera-done"><Check className="h-4 w-4" /> {tx("Klaar")} ({shots.length})</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
